import { AnalyticsBrowser } from "@segment/analytics-next"

import type { EventName, EventsMap } from "./analytics-events"
import { isProduction } from "./environment"
import { captureEvent } from "./error-helpers"

type Config = {
  token: string
  userId?: string
  accountId?: string
  accountType?: string
  organizationId?: string
  cdnURL?: string
}

type AnalyticsInstance = {
  identify: (userId: string, traits?: Record<string, unknown>) => void
  track: (event: string, props?: Record<string, unknown>) => void
  page: (path: string) => void
}

// Lightweight wrapper to minimise wire-up inside consumers,
// and keep vendor-specific implementation out of them.
// By using a factory function,
// we retain the ability to get the config from async calls throughout app boot.
// This is purposely simple (no adapters/plugins) for now.
const createAnalytics = (config?: Config): AnalyticsInstance => {
  const isConfigured = Boolean(config)
  const segment = new AnalyticsBrowser()

  if (config) {
    // EU endpoint gets configured via the Segment UI settings,
    // see https://segment.com/docs/guides/regional-segment/
    segment.load({
      writeKey: config?.token,
      cdnURL: config.cdnURL,
    })
    segment.debug(!isProduction)

    // Identify the user (or generate an identifier for null users).
    // The default properties should take care of the group association.
    // The group association is made in server-side tracking.
    if (config.userId) {
      void segment.identify(config.userId)
    }
  }

  return {
    identify: (userId: string, traits?: Record<string, unknown>) => {
      if (!isConfigured) {
        return
      }

      void segment.identify(userId, {
        account_id: config.accountId,
        organization_id: config.organizationId,
        ...traits,
      })
    },
    track: (event: string, props?: Record<string, unknown>) => {
      if (!isProduction) {
        console.info(`%c${event}`, "font-weight: bold;", props)
      }

      if (!isConfigured) {
        return
      }

      // We also track default properties because Mixpanel isn't great at joining user/account properties with events for analytics
      // TODO Incorporate page context
      void segment.track(
        event,
        {
          ...props,
          account_id: config.accountId,
          organization_id: config.organizationId,
        },
        {
          traits: {
            account_type: config.accountType,
          },
        },
      )

      // Track in application error monitoring for additional context
      // in case an error is caused soon after this event
      captureEvent({ event, ...(props || {}) }, "info")
    },
    page: (path: string) => {
      if (!isProduction) {
        console.info(`%cPAGE: ${path}`, "font-weight: bold;")
      }

      if (!isConfigured) {
        return
      }

      void segment.page(path, {
        account_id: config.accountId,
        organization_id: config.organizationId,
        url: path, // Segment convention
        normalized_path: path, // Mixpanel legacy convention
        context: {
          traits: {
            account_type: config.accountType,
          },
        },
      })
    },
  }
}

// Exposing analytics as global to enable tracking outside of React app.
// Remove this once the app becomes a full SPA.
// Initialise even when analytics is disabled (config is null)
const initAnalytics = (config?: Config) => {
  if (!window.runnAnalytics) {
    const analytics = createAnalytics(config)

    if (typeof window.RAILS_TEMPLATE_ROUTE === "string") {
      analytics.page(window.RAILS_TEMPLATE_ROUTE)
    }

    window.runnAnalytics = analytics
  }
}

// Works around static binding issues: This gets executed
// before the app had a chance to assign the window object.
const getAnalytics = () => {
  return window.runnAnalytics as AnalyticsInstance
}

const identify = (userId: string, traits?: Record<string, unknown>) =>
  getAnalytics().identify(userId, traits)

const track = <E extends EventName>(
  event: E,
  ...[props]: EventsMap[E] extends undefined ? [] : [EventsMap[E]]
) => getAnalytics().track(event, props)

const page = (path: string) => getAnalytics().page(path)

export { initAnalytics, identify, track, page }
